import { inject, Injectable } from '@angular/core';
import type { Params, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { PagePath } from '../enums/page-path.enum';
import type { County } from '../models/county.model';
import type { DeepLinkingParams } from '../models/deep-linking.model';
import { PassParams, type PassParamsMap } from '../models/pass-parameter.model';
import { ConfigResourceService } from './config-resource.service';
import { CountiesService } from './counties.service';
import { DeepLinkingParamsStateService } from './deep-linking-params-state.service';
import { DeepLinkingService } from './deep-linking.service';
import { DemographicsStateService } from './demographics-state.service';
import { NavigationUtilityService } from './navigation-utility.service';
import { NavigationService } from './navigation.service';

@Injectable({ providedIn: 'root' })
// This service is used to process deep linking query parameters and sso values before page loads
export class PassParameterService {
  private readonly configService = inject(ConfigResourceService);
  private readonly demographicsStateService = inject(DemographicsStateService);
  private readonly countiesService = inject(CountiesService);
  private readonly deepLinkingParamsStateService = inject(DeepLinkingParamsStateService);
  private readonly deepLinkingService = inject(DeepLinkingService);
  private readonly navigationService = inject(NavigationService);

  private readonly ZIP_AND_FIPS_REQUIRED_LENGTH: number = 5;
  private readonly MINIMUM_COUNTY_LENGTH: number = 1;

  createFromDeepLinking(deepLinkingParams: DeepLinkingParams): PassParams | null {
    return PassParams.createFromDeepLinking(deepLinkingParams);
  }

  async processParameters(passParametersMap: PassParamsMap): Promise<UrlTree | null> {
    if (passParametersMap?.deepLinkingPassParams && Object.keys(passParametersMap.deepLinkingPassParams).length) {
      const { deepLinkingPassParams } = passParametersMap;

      if (deepLinkingPassParams.TFN) {
        await firstValueFrom(this.deepLinkingParamsStateService.setTFN$(deepLinkingPassParams.TFN));
      }

      return await this.loadZipFips(deepLinkingPassParams);
    }

    return null;
  }

  canProcessQueryString(pagePaths: string[], queryParams: Params): boolean {
    const isDeepLinkingEnabled: boolean =
      this.configService.getProcessedResourceValue<string>('MDC6IsDeepLinkingEnabled', 'true')?.toLowerCase() === 'true';
    const allowablePages = this.deepLinkingService.getAllowablePages();
    return isDeepLinkingEnabled && !!pagePaths?.length && allowablePages.some(pageName => pagePaths.includes(pageName) && !!queryParams);
  }

  resolveParameters(pagePaths: string[], queryParams: Params): PassParamsMap {
    const passParamsMap: PassParamsMap = {};

    if (this.canProcessQueryString(pagePaths, queryParams)) {
      const deepLinkingParams = NavigationUtilityService.convertParamsToUpperCase(queryParams);
      passParamsMap.deepLinkingPassParams = this.createFromDeepLinking(deepLinkingParams);
    }

    return passParamsMap;
  }

  hasDeepLinkingPassParams(passParam: PassParams | null): boolean {
    return !!passParam && !!Object.keys(passParam).length;
  }

  createPassParamsFromDeepLinkingValues(params: Params): PassParams | null {
    const deepLinkingParams = NavigationUtilityService.convertParamsToUpperCase(params);
    return PassParams.createFromDeepLinking(deepLinkingParams);
  }

  private async loadZipFips(passParameters: PassParams): Promise<UrlTree | null> {
    const selectedZip: string | undefined = passParameters.ZipCode || passParameters.Zip;
    const selectedFips: string | undefined = passParameters.CountyFips || passParameters.Fips;

    if (!this.demographicsStateService.hasSelectedZipAndFips(selectedZip, selectedFips)) {
      const validZipAndFips: boolean = await this.setValidZipAndFips(selectedZip, selectedFips);

      return !validZipAndFips ? this.redirectToHome() : null;
    }

    return null;
  }

  private async setValidZipAndFips(zip?: string, fips?: string): Promise<boolean> {
    let isValid: boolean = false;

    if (zip?.length === this.ZIP_AND_FIPS_REQUIRED_LENGTH) {
      await firstValueFrom(this.demographicsStateService.setDemographics$(zip, ''));

      try {
        const counties: County[] = await firstValueFrom(this.countiesService.getCountiesByZip$(zip));

        if (counties?.length > 0) {
          // Handle multi-county zip
          if (
            counties.length > this.MINIMUM_COUNTY_LENGTH &&
            fips?.length === this.ZIP_AND_FIPS_REQUIRED_LENGTH &&
            counties.some(county => county.countyFIPS === fips)
          ) {
            await firstValueFrom(this.demographicsStateService.setDemographics$(zip, fips));
            isValid = true;
          } else if (counties.length === this.MINIMUM_COUNTY_LENGTH) {
            const fetchedFips: string = counties[0].countyFIPS;

            if (fetchedFips) {
              await firstValueFrom(this.demographicsStateService.setDemographics$(zip, fetchedFips));
              isValid = true;
            }
          }
        }
      } catch (error) {
        console.error('Error setting ZIP/FIPS from deep linking:', error);
        // Don't throw - return false to indicate failure
      }
    }

    return isValid;
  }

  private redirectToHome(queryParams?: Params): UrlTree {
    return this.navigationService.constructUrlTreeFromPagePath(PagePath.HOME, { queryParams });
  }
}
